import { useCallback, useEffect, useState } from "react";
import { getReviewerHcWorkflow } from "./workflow-api";
import { filterHcRoutes } from "./reviewer-hc-workflow";

export function useReviewerHcWorkflow(getAccessToken, enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const latest = await getReviewerHcWorkflow(getAccessToken);
      setData(latest);
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "HC approver directory is unavailable.");
    } finally {
      setLoading(false);
    }
  }, [enabled, getAccessToken]);
  useEffect(() => {
    if (enabled) void refresh();
    else { setData(null); setError(""); }
  }, [enabled, refresh]);
  return { data, loading, error, refresh };
}

function statusLabel(route) {
  if (route.status === "active") return "BU HC route";
  if (route.status === "central_fallback") return "Central HC fallback";
  if (route.status === "disabled") return "Disabled — no HC route";
  if (route.status === "configuration_error") return "Routing needs attention";
  return "No active HC route";
}

function People({ approvers }) {
  if (!approvers?.length) return <span className="hc-workflow-none">No active HC approvers</span>;
  return <ul className="hc-workflow-people">{approvers.map((person, index) =>
    <li key={person.email || index}>
      <strong>{person.name || "Unavailable HC approver"}</strong>
      {person.email && <small>{person.email}</small>}
      {!person.resolved && <small>Directory details unavailable</small>}
    </li>)}</ul>;
}

export default function ReviewerHcWorkflow({ workflow }) {
  const [query, setQuery] = useState("");
  const data = workflow.data;
  const visible = filterHcRoutes(data?.routes, query);
  return <section className="hc-workflow-directory" aria-label="HC approval workflow by Business Unit">
    <div className="hc-workflow-directory-heading">
      <div>
        <h3>HC approval workflow by BU</h3>
        <p>Current HC routing contacts by Business Unit. Each person also needs the HC.Approver role; the first authorized HC decision completes this stage.</p>
      </div>
      <button type="button" onClick={workflow.refresh} disabled={workflow.loading}>{workflow.loading ? "Refreshing…" : "Refresh approvers"}</button>
    </div>
    {workflow.loading && !data && <p className="hc-workflow-notice" role="status">Loading HC approvers from Microsoft 365…</p>}
    {workflow.error && <div className="hc-workflow-unavailable" role="alert">
      <strong>HC approver directory unavailable</strong>
      <p>{workflow.error}</p>
      <small>The request register remains available. Existing approvals and permissions are unchanged.</small>
    </div>}
    {data && <>
      <label className="hc-workflow-search">
        Search Business Unit or HC approver
        <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by BU, person or email" />
      </label>
      <div className="hc-workflow-table-wrap" tabIndex={0} role="region" aria-label="Business Unit HC approver directory">
        <table className="hc-workflow-table">
          <thead><tr><th scope="col">Business Unit</th><th scope="col">Configured HC approver(s)</th><th scope="col">Routing</th><th scope="col">After HC approval</th></tr></thead>
          <tbody>{visible.map(route => <tr key={route.companyKey}>
            <td><strong>{route.companyKey}</strong></td>
            <td><People approvers={route.approvers} /></td>
            <td><span className="hc-workflow-status">{statusLabel(route)}</span></td>
            <td>{route.status === "active" ? (route.ccdApprovalRequired ? "Central CCD review" : "Microsoft 365 update (CCD skipped)") : route.status === "central_fallback" ? "Central CCD review" : "Routing needs attention"}</td>
          </tr>)}</tbody>
        </table>
      </div>
      {!visible.length && <p className="hc-workflow-notice">{data.routes?.length ? "No Business Units match the search." : "No Business Unit HC routes are configured."}</p>}
      <div className="hc-workflow-fallback">
        <strong>Central HC fallback</strong>
        <span>{data.centralFallback?.configured ? "Used for BUs without an active dedicated HC route." : "Not configured."}</span>
        {data.centralFallback?.configured && <People approvers={data.centralFallback.approvers} />}
      </div>
      <small className="hc-workflow-fineprint">Read-only. Assignments reflect the current routing configuration, not historical approval ownership or proof that an email was opened. Completed decisions are recorded separately in the workflow log.</small>
    </>}
  </section>;
}
